<template>
  <!-- Sticky header + safe-area fill (iOS notch) -->
  <header class="sticky top-0 z-[999] pt-[env(safe-area-inset-top)]">
    <div class="border-b border-white/10 bg-black/80 backdrop-blur-xl">
      <nav aria-label="Primary" class="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
        <div class="relative flex h-16 items-center justify-between">
          <!-- LEFT -->
          <div class="flex items-center gap-2">
            <!-- Mobile burger -->
            <button
              type="button"
              class="inline-flex xl:hidden items-center justify-center rounded-xl p-2 text-white/90 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 active:scale-[0.98] transition"
              aria-controls="mobile-menu"
              :aria-expanded="showMobileMenu ? 'true' : 'false'"
              @click.stop="toggleMobileMenu"
            >
              <span class="sr-only">{{ t('nav.a11y.openMainMenu') }}</span>
              <s-icon v-if="!showMobileMenu" icon="view-list" dimension="28" />
              <s-icon v-else icon="x" dimension="28" />
            </button>

            <!-- Desktop logo -->
            <NuxtLink
              :to="localePath('/')"
              class="hidden xl:flex items-center gap-2 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              data-cy="home-logo-desktop"
              :aria-label="t('nav.a11y.home')"
              @click="hideAll"
            >
              <img
                class="h-9 w-auto"
                src="/images/icons/idmanify-logo.svg"
                alt="Idmanify"
                height="36"
                width="102"
                loading="eager"
                decoding="async"
                fetchpriority="high"
              />
            </NuxtLink>

            <!-- Desktop nav -->
            <div class="hidden xl:flex items-center gap-1 ml-2">
              <!-- HOME -->
              <NuxtLink
                :to="localePath('/')"
                prefetch
                class="group relative rounded-xl px-3 py-2 text-sm font-semibold uppercase tracking-wide text-white/80 hover:text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 transition"
                :class="isActivePath('/') ? 'bg-white/10 text-white' : ''"
                @click="hideAll"
              >
                {{ t('topbar.home') }}
                <span
                  class="pointer-events-none absolute inset-x-3 -bottom-[2px] h-[2px] rounded-full bg-orange-500/0 group-hover:bg-orange-500/60 transition"
                  :class="isActivePath('/') ? '!bg-orange-500/70' : ''"
                />
              </NuxtLink>

              <!-- PROGRAMS (Mega Menu) -->
              <div
                class="relative"
                @mouseenter="setExpanded('programs', true)"
                @mouseleave="setExpanded('programs', false)"
              >
                <button
                  type="button"
                  class="group inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold uppercase tracking-wide text-white/80 hover:text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 transition"
                  :class="isProgramsActive ? 'bg-white/10 text-white' : ''"
                  aria-haspopup="true"
                  :aria-expanded="isExpanded('programs') ? 'true' : 'false'"
                  @click.stop="toggleExpanded('programs')"
                >
                  <span>{{ t('topbar.programs') }}</span>
                  <s-icon
                    :icon="
                      isExpanded('programs') ? 'chevron-up' : 'chevron-down'
                    "
                    dimension="14"
                  />
                  <span
                    class="pointer-events-none absolute inset-x-3 -bottom-[2px] h-[2px] rounded-full bg-orange-500/0 group-hover:bg-orange-500/60 transition"
                    :class="isProgramsActive ? '!bg-orange-500/70' : ''"
                  />
                </button>

                <transition name="fade-scale">
                  <div
                    v-if="isExpanded('programs')"
                    class="absolute left-0 mt-3 w-[60rem] overflow-hidden rounded-3xl border border-white/10 bg-neutral-950/95 shadow-[0_24px_80px_-35px_rgba(0,0,0,0.95)]"
                    role="menu"
                    aria-orientation="vertical"
                  >
                    <!-- Top bar -->
                    <div
                      class="flex items-center justify-between gap-3 border-b border-white/10 p-3"
                    >
                      <div class="flex items-center gap-2">
                        <div
                          class="h-9 w-9 rounded-2xl bg-white/10 flex items-center justify-center"
                        >
                          <span class="text-white/90 text-sm font-bold">{{
                            t('nav.programs.badgeLetter')
                          }}</span>
                        </div>
                        <div>
                          <div class="text-sm font-semibold text-white">
                            {{ t('nav.programs.title') }}
                          </div>
                          <div class="text-xs text-white/60">
                            {{
                              packagesPending
                                ? t('nav.programs.loading')
                                : t('nav.programs.availableCount', {
                                    count: packagesCount
                                  })
                            }}
                          </div>
                        </div>
                      </div>

                      <!-- Search -->
                      <div class="relative w-[20rem]">
                        <input
                          v-model="programsQuery"
                          type="text"
                          class="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-white/25 focus:ring-2 focus:ring-white/10"
                          :placeholder="t('nav.programs.searchPlaceholder')"
                        />
                        <div
                          class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white/40 text-xs"
                        >
                          {{ t('nav.programs.searchHint') }}
                        </div>
                      </div>
                    </div>

                    <!-- Body -->
                    <div class="grid grid-cols-[18rem_1fr]">
                      <!-- Left: categories (with tones) -->
                      <div class="border-r border-white/10 p-2">
                        <button
                          type="button"
                          class="w-full rounded-2xl px-3 py-2 text-left text-sm font-semibold transition"
                          :class="
                            activeProgramGroup === 'ALL'
                              ? 'bg-white/10 text-white'
                              : 'text-white/80 hover:bg-white/5 hover:text-white'
                          "
                          @click="activeProgramGroup = 'ALL'"
                        >
                          {{ t('nav.programs.allPrograms') }}
                        </button>

                        <div class="mt-2 space-y-1">
                          <button
                            v-for="g in programsGroups"
                            :key="g.id"
                            type="button"
                            class="group w-full rounded-2xl px-3 py-2 text-left text-sm font-semibold transition ring-1 ring-transparent"
                            :class="categoryButtonClass(g.type)"
                            @click="activeProgramGroup = g.type"
                          >
                            <div
                              class="flex items-center justify-between gap-2"
                            >
                              <span class="line-clamp-1">{{ g.label }}</span>
                              <span class="text-xs opacity-75">{{
                                g.count
                              }}</span>
                            </div>
                          </button>
                        </div>

                        <!-- SSR fail hint -->
                        <div
                          v-if="packagesSsrEmptyButClientFilled"
                          class="mt-3 rounded-2xl border border-orange-500/20 bg-orange-500/10 p-3 text-xs text-orange-100/90"
                        >
                          {{ t('nav.programs.ssrFallbackHint') }}
                        </div>
                      </div>

                      <!-- Right: program tiles -->
                      <div class="p-3">
                        <!-- Loading skeleton -->
                        <div
                          v-if="packagesPending && !packagesCount"
                          class="grid grid-cols-2 gap-2"
                        >
                          <div
                            v-for="i in 8"
                            :key="i"
                            class="h-16 rounded-2xl bg-white/5 animate-pulse"
                          />
                        </div>

                        <!-- Empty -->
                        <div
                          v-else-if="filteredPrograms.length === 0"
                          class="rounded-2xl border border-white/10 bg-white/5 p-6 text-center"
                        >
                          <div class="text-white font-semibold">
                            {{ t('nav.programs.noResultsTitle') }}
                          </div>
                          <div class="mt-1 text-sm text-white/60">
                            {{ t('nav.programs.noResultsText') }}
                          </div>
                        </div>

                        <!-- Programs -->
                        <div
                          v-else
                          class="max-h-[22rem] overflow-auto pr-2 [scrollbar-width:thin]"
                        >
                          <div class="grid grid-cols-2 gap-2">
                            <NuxtLink
                              v-for="p in filteredPrograms"
                              :key="p.id"
                              :to="localePath(p.link)"
                              prefetch
                              class="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white/85 transition hover:text-white hover:border-white/15 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                              @click="hideAll"
                            >
                              <span
                                aria-hidden="true"
                                class="pointer-events-none absolute -right-20 -top-20 h-44 w-44 rounded-full blur-3xl opacity-0 transition group-hover:opacity-60"
                                :class="programGlowClass(p.type)"
                              />
                              <div
                                class="flex items-start justify-between gap-3"
                              >
                                <div class="min-w-0">
                                  <div
                                    class="text-sm font-semibold line-clamp-2"
                                  >
                                    {{ p.label }}
                                  </div>
                                  <div class="mt-0.5 flex items-center gap-2">
                                    <span
                                      class="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset"
                                      :class="programBadgeClass(p.type)"
                                    >
                                      {{ p.groupLabel }}
                                    </span>
                                  </div>
                                </div>
                                <span
                                  class="text-white/25 group-hover:text-white/60 transition"
                                  >{{ t('nav.ui.externalHint') }}</span
                                >
                              </div>
                            </NuxtLink>
                          </div>
                        </div>

                        <!-- Footer CTA -->
                        <!--                        <div
                          class="mt-3 flex items-center justify-between rounded-2xl border border-white/10 bg-gradient-to-r from-white/5 to-white/0 px-4 py-3"
                        >
                          <div class="text-sm text-white/80">
                            <span class="font-semibold text-white">{{
                              t('nav.programs.proTipLabel')
                            }}</span>
                            {{ t('nav.programs.proTipText') }}
                          </div>
                          <NuxtLink
                            :to="localePath('/programs')"
                            class="rounded-2xl bg-white/10 px-3 py-2 text-sm font-semibold text-white hover:bg-white/15 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                            @click="hideAll"
                          >
                            {{ t('nav.programs.viewAll') }}
                          </NuxtLink>
                        </div>-->
                      </div>
                    </div>
                  </div>
                </transition>
              </div>

              <!-- SUCCESS STORIES -->
              <NuxtLink
                :to="localePath({ name: 'stories' })"
                :aria-current="isStoriesActive ? 'page' : undefined"
                data-cy="nav-stories"
                prefetch
                class="group relative rounded-xl px-3 py-2 text-sm font-semibold uppercase tracking-wide text-white/80 hover:text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 transition"
                :class="isStoriesActive ? 'bg-white/10 text-white' : ''"
                @click="hideAll"
              >
                {{ t('topbar.successStories') }}
                <span
                  class="pointer-events-none absolute inset-x-3 -bottom-[2px] h-[2px] rounded-full bg-orange-500/0 group-hover:bg-orange-500/60 transition"
                  :class="isStoriesActive ? '!bg-orange-500/70' : ''"
                />
              </NuxtLink>

              <!-- TOOLS (Mini Mega Menu) -->
              <div
                class="relative"
                @mouseenter="setExpanded('tools', true)"
                @mouseleave="setExpanded('tools', false)"
              >
                <button
                  type="button"
                  class="group inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold uppercase tracking-wide text-white/80 hover:text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 transition"
                  :class="isToolsActive ? 'bg-white/10 text-white' : ''"
                  aria-haspopup="true"
                  :aria-expanded="isExpanded('tools') ? 'true' : 'false'"
                  @click.stop="toggleExpanded('tools')"
                >
                  <span>{{ t('topbar.tools') }}</span>
                  <s-icon
                    :icon="isExpanded('tools') ? 'chevron-up' : 'chevron-down'"
                    dimension="14"
                  />
                  <span
                    class="pointer-events-none absolute inset-x-3 -bottom-[2px] h-[2px] rounded-full bg-orange-500/0 group-hover:bg-orange-500/60 transition"
                    :class="isToolsActive ? '!bg-orange-500/70' : ''"
                  />
                </button>

                <transition name="fade-scale">
                  <div
                    v-if="isExpanded('tools')"
                    class="absolute left-0 mt-3 w-[34rem] overflow-hidden rounded-3xl border border-white/10 bg-neutral-950/95 shadow-[0_24px_80px_-35px_rgba(0,0,0,0.95)]"
                    role="menu"
                    aria-orientation="vertical"
                  >
                    <!-- Header -->
                    <div
                      class="flex items-center justify-between gap-3 border-b border-white/10 p-3"
                    >
                      <div class="flex items-center gap-2">
                        <div
                          class="h-9 w-9 rounded-2xl bg-white/10 flex items-center justify-center"
                        >
                          <span class="text-white/90 text-sm font-bold">{{
                            t('nav.tools.badgeLetter')
                          }}</span>
                        </div>
                        <div>
                          <div class="text-sm font-semibold text-white">
                            {{ t('topbar.tools') }}
                          </div>
                          <div class="text-xs text-white/60">
                            {{ t('nav.tools.subtitle') }}
                          </div>
                        </div>
                      </div>
                      <div class="text-xs text-white/50">
                        {{ t('nav.tools.headerHint') }}
                      </div>
                    </div>

                    <!-- Cards -->
                    <div class="p-3">
                      <div class="grid grid-cols-2 gap-2">
                        <NuxtLink
                          :to="localePath('/product-suggestion')"
                          prefetch
                          class="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-4 text-white/85 transition hover:bg-white/10 hover:text-white hover:border-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                          :class="
                            isActivePath('/product-suggestion')
                              ? 'bg-white/10 text-white'
                              : ''
                          "
                          @click="hideAll"
                        >
                          <span
                            aria-hidden="true"
                            class="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full blur-3xl opacity-60 bg-orange-500/25"
                          />
                          <div class="flex items-start justify-between gap-3">
                            <div class="min-w-0">
                              <div class="text-sm font-semibold">
                                {{ t('tools.productSuggestion') }}
                              </div>
                              <div
                                class="mt-1 text-xs text-white/60 line-clamp-2"
                              >
                                {{
                                  t('nav.tools.cards.productSuggestion.desc')
                                }}
                              </div>
                            </div>
                            <span
                              class="text-white/25 group-hover:text-white/60 transition"
                              >{{ t('nav.ui.externalHint') }}</span
                            >
                          </div>
                          <div
                            class="mt-3 inline-flex items-center gap-2 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white/80 ring-1 ring-inset ring-white/10"
                          >
                            {{ t('nav.tools.cards.productSuggestion.badge') }}
                          </div>
                        </NuxtLink>

                        <NuxtLink
                          :to="localePath('/daily-calorie-requirements')"
                          prefetch
                          class="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-4 text-white/85 transition hover:bg-white/10 hover:text-white hover:border-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                          :class="
                            isActivePath('/daily-calorie-requirements')
                              ? 'bg-white/10 text-white'
                              : ''
                          "
                          @click="hideAll"
                        >
                          <span
                            aria-hidden="true"
                            class="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full blur-3xl opacity-60 bg-sky-500/25"
                          />
                          <div class="flex items-start justify-between gap-3">
                            <div class="min-w-0">
                              <div class="text-sm font-semibold">
                                {{ t('tools.calorieRequirement') }}
                              </div>
                              <div
                                class="mt-1 text-xs text-white/60 line-clamp-2"
                              >
                                {{
                                  t('nav.tools.cards.calorieCalculator.desc')
                                }}
                              </div>
                            </div>
                            <span
                              class="text-white/25 group-hover:text-white/60 transition"
                              >{{ t('nav.ui.externalHint') }}</span
                            >
                          </div>
                          <div
                            class="mt-3 inline-flex items-center gap-2 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white/80 ring-1 ring-inset ring-white/10"
                          >
                            {{ t('nav.tools.cards.calorieCalculator.badge') }}
                          </div>
                        </NuxtLink>
                      </div>

                      <!-- Footer -->
                      <div
                        class="mt-3 flex items-center justify-between rounded-2xl border border-white/10 bg-gradient-to-r from-white/5 to-white/0 px-4 py-3"
                      >
                        <div class="text-sm text-white/80">
                          <span class="font-semibold text-white">{{
                            t('nav.tools.footer.title')
                          }}</span>
                          {{ t('nav.tools.footer.text') }}
                        </div>
                        <div class="text-xs text-white/50">
                          {{ t('nav.tools.footer.hint') }}
                        </div>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>

              <!-- BLOG -->
              <NuxtLink
                :to="localePath('/blog')"
                prefetch
                class="group relative rounded-xl px-3 py-2 text-sm font-semibold uppercase tracking-wide text-white/80 hover:text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 transition"
                :class="isActivePath('/blog') ? 'bg-white/10 text-white' : ''"
                @click="hideAll"
              >
                {{ t('topbar.blog') }}
                <span
                  class="pointer-events-none absolute inset-x-3 -bottom-[2px] h-[2px] rounded-full bg-orange-500/0 group-hover:bg-orange-500/60 transition"
                  :class="isActivePath('/blog') ? '!bg-orange-500/70' : ''"
                />
              </NuxtLink>
            </div>
          </div>

          <!-- CENTER LOGO (mobile only - pixel perfect center) -->
          <div
            class="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 xl:hidden"
          >
            <NuxtLink
              :to="localePath('/')"
              class="pointer-events-auto rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              :aria-label="t('nav.a11y.home')"
              @click="hideAll"
            >
              <img
                class="h-9 w-auto drop-shadow-[0_10px_28px_rgba(0,0,0,0.55)]"
                src="/images/icons/idmanify-logo.svg"
                alt="Idmanify"
                height="36"
                width="102"
                loading="eager"
                decoding="async"
                fetchpriority="high"
              />
            </NuxtLink>
          </div>

          <!-- RIGHT -->
          <div class="flex items-center gap-2">
            <!-- Language -->
            <div class="relative">
              <button
                id="language-menu"
                type="button"
                class="inline-flex items-center justify-center rounded-xl p-2 text-white/90 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 active:scale-[0.98] transition"
                aria-controls="language-options"
                :aria-expanded="languageDropdown ? 'true' : 'false'"
                @click.stop="languageDropdown = !languageDropdown"
              >
                <span class="sr-only">{{ t('nav.a11y.language') }}</span>
                <img
                  class="h-6 w-6 rounded-md object-contain"
                  :src="currentFlagSrc"
                  :alt="currentFlagAlt"
                  height="24"
                  width="24"
                  loading="eager"
                  decoding="async"
                  fetchpriority="low"
                />
              </button>

              <transition name="fade-scale">
                <nav
                  v-show="languageDropdown"
                  id="language-options"
                  class="language-options absolute right-0 mt-3 w-14 overflow-y-auto overscroll-contain rounded-2xl border border-white/10 bg-neutral-950/95 shadow-[0_24px_80px_-35px_rgba(0,0,0,0.95)]"
                  aria-labelledby="language-menu"
                >
                  <template v-if="isBlogListing">
                    <NuxtLink
                      v-for="loc in availableLocales"
                      :key="`${languageLinksRevision}:${loc}`"
                      :to="localePath('/blog', loc)"
                      :prefetch="false"
                      :hreflang="loc"
                      :data-locale="loc"
                      class="flex min-h-11 w-full items-center justify-center p-2 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/60 transition"
                      @click.capture="rememberLanguage($event, loc)"
                      @auxclick.capture="rememberLanguage($event, loc)"
                      @contextmenu.capture="rememberLanguage($event, loc)"
                    >
                      <span class="sr-only">{{
                        t('nav.a11y.switchLanguageTo', {
                          locale: loc.toUpperCase()
                        })
                      }}</span>
                      <img
                        class="h-7 w-7 rounded-md object-contain"
                        :src="flagSrc(loc)"
                        :alt="flagAlt(loc)"
                        width="28"
                        height="28"
                        loading="lazy"
                        decoding="async"
                      />
                    </NuxtLink>
                  </template>
                  <template v-else>
                    <SwitchLocalePathLink
                      v-for="loc in availableLocales"
                      :key="`${languageLinksRevision}:${loc}`"
                      :locale="loc"
                      :prefetch="false"
                      :hreflang="loc"
                      :data-locale="loc"
                      class="flex min-h-11 w-full items-center justify-center p-2 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/60 transition"
                      @click.capture="rememberLanguage($event, loc)"
                      @auxclick.capture="rememberLanguage($event, loc)"
                      @contextmenu.capture="rememberLanguage($event, loc)"
                    >
                      <span class="sr-only">{{
                        t('nav.a11y.switchLanguageTo', {
                          locale: loc.toUpperCase()
                        })
                      }}</span>
                      <img
                        class="h-7 w-7 rounded-md object-contain"
                        :src="flagSrc(loc)"
                        :alt="flagAlt(loc)"
                        width="28"
                        height="28"
                        loading="lazy"
                        decoding="async"
                      />
                    </SwitchLocalePathLink>
                  </template>
                </nav>
              </transition>
            </div>

            <!-- User -->
            <div class="relative">
              <button
                id="user-menu"
                type="button"
                class="inline-flex items-center justify-center rounded-xl p-1.5 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 active:scale-[0.98] transition"
                aria-haspopup="true"
                :aria-expanded="userMenuDropdown ? 'true' : 'false'"
                @click.stop="userMenuDropdown = !userMenuDropdown"
              >
                <span class="sr-only">{{ t('nav.a11y.userMenu') }}</span>

                <img
                  v-if="isLoggedIn && userPhoto"
                  class="h-9 w-9 rounded-xl object-cover"
                  :src="userPhoto"
                  :alt="t('nav.a11y.userAvatar')"
                  loading="lazy"
                />

                <div
                  v-else-if="isLoggedIn && !userPhoto"
                  class="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 text-sm font-bold text-white"
                  :aria-label="t('nav.a11y.userInitials')"
                >
                  <span>{{ userInitials.first }}</span
                  ><span>{{ userInitials.last }}</span>
                </div>

                <s-icon
                  v-else
                  icon="user"
                  dimension="28"
                  classes="text-white/90"
                />
              </button>

              <transition name="fade-scale">
                <div
                  v-if="userMenuDropdown"
                  class="absolute right-0 mt-3 w-56 overflow-hidden rounded-2xl border border-white/10 bg-neutral-950/95 shadow-[0_24px_80px_-35px_rgba(0,0,0,0.95)]"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="user-menu"
                >
                  <div class="p-1">
                    <NuxtLink
                      v-if="isLoggedIn"
                      :to="localePath('/userpanel')"
                      class="block rounded-xl px-3 py-2 text-sm font-medium text-white/80 hover:bg-white/10 hover:text-white transition"
                      @click="hideAll"
                    >
                      {{ t('topbar.userMenu.profile') }}
                    </NuxtLink>

                    <button
                      v-if="isLoggedIn"
                      type="button"
                      class="w-full rounded-xl px-3 py-2 text-left text-sm font-medium text-white/80 hover:bg-white/10 hover:text-white transition"
                      @click="logoutAndClose"
                    >
                      {{ t('topbar.userMenu.logout') }}
                    </button>

                    <NuxtLink
                      v-if="!isLoggedIn"
                      :to="localePath({ name: 'login' })"
                      class="block rounded-xl px-3 py-2 text-sm font-medium text-white/80 hover:bg-white/10 hover:text-white transition"
                      @click="hideAll"
                    >
                      {{ t('actions.signIn') }}
                    </NuxtLink>

                    <NuxtLink
                      v-if="!isLoggedIn"
                      :to="localePath({ name: 'signup' })"
                      class="block rounded-xl px-3 py-2 text-sm font-medium text-white/80 hover:bg-white/10 hover:text-white transition"
                      @click="hideAll"
                    >
                      {{ t('actions.signUp') }}
                    </NuxtLink>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>

        <!-- Mobile menu -->
        <transition name="slide-down">
          <div v-if="showMobileMenu" id="mobile-menu" class="xl:hidden pb-4">
            <div
              class="mt-3 rounded-3xl border border-white/10 bg-neutral-950/60 p-2 shadow-[0_24px_80px_-35px_rgba(0,0,0,0.95)]"
            >
              <!--              <div class="p-2">
                <input
                  v-model="programsQuery"
                  type="text"
                  class="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/40 outline-none focus:border-white/25 focus:ring-2 focus:ring-white/10"
                  :placeholder="t('nav.programs.searchPlaceholder')"
                />
              </div>-->

              <NuxtLink
                :to="localePath('/')"
                class="block rounded-2xl px-3 py-3 text-base font-semibold text-white/85 hover:bg-white/10 hover:text-white transition"
                :class="isActivePath('/') ? 'bg-white/10 text-white' : ''"
                @click="hideAll"
              >
                {{ t('topbar.home') }}
              </NuxtLink>

              <!-- Programs accordion (default OPEN) -->
              <div class="mt-1">
                <button
                  type="button"
                  class="flex w-full items-center justify-between rounded-2xl px-3 py-3 text-base font-semibold text-white/85 hover:bg-white/10 hover:text-white transition"
                  :class="isProgramsActive ? 'bg-white/10 text-white' : ''"
                  :aria-expanded="isExpanded('m-programs') ? 'true' : 'false'"
                  @click.stop="toggleExpanded('m-programs')"
                >
                  <span>{{ t('topbar.programs') }}</span>
                  <s-icon
                    :icon="
                      isExpanded('m-programs') ? 'chevron-up' : 'chevron-down'
                    "
                    dimension="14"
                  />
                </button>

                <transition name="fade">
                  <div v-if="isExpanded('m-programs')" class="px-2 pb-2">
                    <!-- category chips -->
                    <div
                      class="mt-1 flex gap-2 overflow-auto pb-1 [scrollbar-width:thin]"
                    >
                      <button
                        type="button"
                        class="shrink-0 rounded-2xl px-3 py-2 text-sm font-semibold transition"
                        :class="
                          activeProgramGroup === 'ALL'
                            ? 'bg-white/10 text-white'
                            : 'bg-white/5 text-white/75 hover:bg-white/10 hover:text-white'
                        "
                        @click="activeProgramGroup = 'ALL'"
                      >
                        {{ t('nav.programs.allShort') }}
                      </button>
                      <button
                        v-for="g in programsGroups"
                        :key="g.id"
                        type="button"
                        class="shrink-0 rounded-2xl px-3 py-2 text-sm font-semibold transition ring-1 ring-inset"
                        :class="categoryChipClass(g.type)"
                        @click="activeProgramGroup = g.type"
                      >
                        {{ g.label }}
                      </button>
                    </div>

                    <div
                      v-if="packagesPending && !packagesCount"
                      class="grid grid-cols-1 gap-2 mt-2"
                    >
                      <div
                        v-for="i in 6"
                        :key="i"
                        class="h-12 rounded-2xl bg-white/5 animate-pulse"
                      />
                    </div>

                    <div
                      v-else-if="filteredPrograms.length === 0"
                      class="mt-2 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/70"
                    >
                      {{ t('nav.programs.noResultsMobile') }}
                    </div>

                    <div v-else class="mt-2 flex flex-col gap-2">
                      <NuxtLink
                        v-for="p in filteredPrograms"
                        :key="p.id"
                        :to="localePath(p.link)"
                        class="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white/85 hover:bg-white/10 hover:text-white transition"
                        @click="hideAll"
                      >
                        <span
                          aria-hidden="true"
                          class="pointer-events-none absolute -right-20 -top-20 h-44 w-44 rounded-full blur-3xl opacity-0 transition group-hover:opacity-60"
                          :class="programGlowClass(p.type)"
                        />
                        <div class="line-clamp-2">{{ p.label }}</div>
                        <div class="mt-1">
                          <span
                            class="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset"
                            :class="programBadgeClass(p.type)"
                          >
                            {{ p.groupLabel }}
                          </span>
                        </div>
                      </NuxtLink>
                    </div>
                  </div>
                </transition>
              </div>

              <!-- Tools accordion mobile -->
              <div class="mt-1">
                <button
                  type="button"
                  class="flex w-full items-center justify-between rounded-2xl px-3 py-3 text-base font-semibold text-white/85 hover:bg-white/10 hover:text-white transition"
                  :class="isToolsActive ? 'bg-white/10 text-white' : ''"
                  :aria-expanded="isExpanded('m-tools') ? 'true' : 'false'"
                  @click.stop="toggleExpanded('m-tools')"
                >
                  <span>{{ t('topbar.tools') }}</span>
                  <s-icon
                    :icon="
                      isExpanded('m-tools') ? 'chevron-up' : 'chevron-down'
                    "
                    dimension="14"
                  />
                </button>

                <transition name="fade">
                  <div v-if="isExpanded('m-tools')" class="px-2 pb-2">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                      <NuxtLink
                        :to="localePath('/product-suggestion')"
                        class="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-4 text-white/85 transition hover:bg-white/10 hover:text-white hover:border-white/15"
                        @click="hideAll"
                      >
                        <span
                          aria-hidden="true"
                          class="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full blur-3xl opacity-60 bg-orange-500/25"
                        />
                        <div class="flex items-start justify-between gap-3">
                          <div class="min-w-0">
                            <div class="text-sm font-semibold">
                              {{ t('tools.productSuggestion') }}
                            </div>
                            <div
                              class="mt-1 text-xs text-white/60 line-clamp-2"
                            >
                              {{ t('nav.tools.cards.productSuggestion.desc') }}
                            </div>
                          </div>
                          <span
                            class="text-white/25 group-hover:text-white/60 transition"
                            >{{ t('nav.ui.externalHint') }}</span
                          >
                        </div>
                        <div
                          class="mt-3 inline-flex items-center rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white/80 ring-1 ring-inset ring-white/10"
                        >
                          {{ t('nav.tools.cards.productSuggestion.badge') }}
                        </div>
                      </NuxtLink>

                      <NuxtLink
                        :to="localePath('/daily-calorie-requirements')"
                        class="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-4 text-white/85 transition hover:bg-white/10 hover:text-white hover:border-white/15"
                        @click="hideAll"
                      >
                        <span
                          aria-hidden="true"
                          class="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full blur-3xl opacity-60 bg-sky-500/25"
                        />
                        <div class="flex items-start justify-between gap-3">
                          <div class="min-w-0">
                            <div class="text-sm font-semibold">
                              {{ t('tools.calorieRequirement') }}
                            </div>
                            <div
                              class="mt-1 text-xs text-white/60 line-clamp-2"
                            >
                              {{ t('nav.tools.cards.calorieCalculator.desc') }}
                            </div>
                          </div>
                          <span
                            class="text-white/25 group-hover:text-white/60 transition"
                            >{{ t('nav.ui.externalHint') }}</span
                          >
                        </div>
                        <div
                          class="mt-3 inline-flex items-center rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white/80 ring-1 ring-inset ring-white/10"
                        >
                          {{ t('nav.tools.cards.calorieCalculator.badge') }}
                        </div>
                      </NuxtLink>
                    </div>
                  </div>
                </transition>
              </div>

              <NuxtLink
                :to="localePath({ name: 'stories' })"
                :aria-current="isStoriesActive ? 'page' : undefined"
                data-cy="nav-stories"
                class="block rounded-2xl px-3 py-3 text-base font-semibold text-white/85 hover:bg-white/10 hover:text-white transition"
                :class="isStoriesActive ? 'bg-white/10 text-white' : ''"
                @click="hideAll"
              >
                {{ t('topbar.successStories') }}
              </NuxtLink>

              <NuxtLink
                :to="localePath('/blog')"
                class="block rounded-2xl px-3 py-3 text-base font-semibold text-white/85 hover:bg-white/10 hover:text-white transition"
                :class="isActivePath('/blog') ? 'bg-white/10 text-white' : ''"
                @click="hideAll"
              >
                {{ t('topbar.blog') }}
              </NuxtLink>

              <!-- Debug (dev only) -->
              <div
                v-if="isDev"
                class="mt-2 rounded-2xl border border-white/10 bg-white/5 p-3 text-xs text-white/70"
              >
                <div class="font-semibold text-white/80">
                  {{ t('nav.debug.title') }}
                </div>
                <div>
                  {{ t('nav.debug.apiUrl') }}:
                  <code class="px-1">{{ apiBase }}</code>
                </div>
                <div>
                  {{ t('nav.debug.ssrClient') }}:
                  <code class="px-1">{{
                    packagesSsrEmptyButClientFilled
                  }}</code>
                </div>
                <div>
                  {{ t('nav.debug.packages') }}:
                  <code class="px-1">{{ packagesCount }}</code>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </nav>
    </div>
  </header>
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch
} from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useAsyncData,
  useNuxtApp,
  useRoute,
  useRuntimeConfig,
  useRequestHeaders,
  navigateTo
} from '#app';
import { useLocalePath, useSwitchLocalePath } from '#i18n';
import { authLogout } from '../composables/auth';

type ApiPackage = {
  id?: number | string;
  uuid?: string;
  title?: string;
  slug?: string;
  type?: string;
  titles?: Record<string, string>;
  slugs?: Record<string, string>;
};

type ProgramLink = {
  id: string;
  label: string;
  link: string;
  type: string;
  groupLabel: string;
};

const nuxtApp = useNuxtApp();
const route = useRoute();
const config = useRuntimeConfig();
const { t, locale } = useI18n();

const localePath = useLocalePath();

const isDev = import.meta.dev;
const supportedLocaleCodes = new Set([
  'en',
  'tr',
  'de',
  'fr',
  'es',
  'pt',
  'it',
  'ar',
  'hi',
  'ur',
  'ru',
  'ja',
  'zh'
]);

function normalizeLocaleCode(value: unknown) {
  const raw = String(value || '')
    .trim()
    .toLowerCase()
    .replace('_', '-');
  const base = raw.split('-')[0];

  return supportedLocaleCodes.has(raw)
    ? raw
    : supportedLocaleCodes.has(base)
      ? base
      : 'en';
}

function flagSrc(value: unknown) {
  return `/images/flags/${normalizeLocaleCode(value)}.svg`;
}

function flagAlt(value: unknown) {
  return normalizeLocaleCode(value).toUpperCase();
}

const currentLocale = computed(() => normalizeLocaleCode(locale.value));
const currentFlagSrc = computed(() => flagSrc(currentLocale.value));
const currentFlagAlt = computed(() => flagAlt(currentLocale.value));
const apiBase = computed(() =>
  String((config.public as any)?.API_URL || '').replace(/\/+$/, '')
);

function joinUrl(base: string, path: string) {
  const clean = path.startsWith('/') ? path : `/${path}`;
  return `${base}${clean}`;
}

function stableId(parts: Array<string | number | undefined | null>) {
  return parts
    .filter((p) => p !== undefined && p !== null && String(p).length > 0)
    .map((p) => String(p))
    .join(':');
}

/* ---------- UI state ---------- */
const userMenuDropdown = ref(false);
const languageDropdown = ref(false);
const languageLinksRevision = ref(0);
// Layouts survive navigation. Recreate the official link resolvers only after
// the incoming async page has finalized its translated params (i18n #4129).
const stopLanguageLinksRefresh = import.meta.client
  ? nuxtApp.hook('page:finish', () => {
      languageLinksRevision.value++;
    })
  : () => {};
const showMobileMenu = ref(false);

const expandedState = reactive(new Map<string, boolean>());
const isExpanded = (id: string) => expandedState.get(id) ?? false;
const setExpanded = (id: string, v: boolean) => expandedState.set(id, v);
const toggleExpanded = (id: string) => setExpanded(id, !isExpanded(id));
const resetExpanded = () => expandedState.clear();

const programsQuery = ref('');
const activeProgramGroup = ref<string>('ALL');

/* Auth safe */
const isLoggedIn = computed(() => Boolean(nuxtApp?.$auth?.loggedIn?.value));
const userPhoto = computed(() => nuxtApp?.$auth?.session?.value?.photo || '');
const userInitials = computed(() => {
  const firstName = nuxtApp?.$auth?.session?.value?.firstName || '';
  const lastName = nuxtApp?.$auth?.session?.value?.lastName || '';
  return {
    first: firstName ? firstName.charAt(0) : '',
    last: lastName ? lastName.charAt(0) : ''
  };
});

/* ---------- Packages normalization ---------- */
function pickLocalizedTitle(p: ApiPackage, loc: string) {
  return (
    p.titles?.[loc] ||
    p.title ||
    (p.titles ? Object.values(p.titles)[0] : '') ||
    ''
  ).trim();
}
function pickLocalizedSlug(p: ApiPackage, loc: string) {
  return (
    p.slugs?.[loc] ||
    p.slug ||
    (p.slugs ? Object.values(p.slugs)[0] : '') ||
    ''
  ).trim();
}
function normalizePackages(raw: any, loc: string) {
  const arr: ApiPackage[] = Array.isArray(raw) ? raw : [];
  return arr
    .filter(Boolean)
    .map((p) => ({
      id: p.id ?? p.uuid,
      title: pickLocalizedTitle(p, loc),
      slug: pickLocalizedSlug(p, loc),
      type: String(p.type ?? '').trim()
    }))
    .filter((p) => p.title && p.slug && p.type);
}

/**
 * ✅ SSR attempt + Client Fallback Refetch
 * - cache: 'no-store' to avoid stale/empty edge cache
 * - Accept-Language + lang query for Laravel localization
 * - small retry on client if SSR returned empty
 */
const packagesKey = computed(() => `nav:packages:${currentLocale.value}`);
const ssrResultWasEmpty = ref(false);
const clientRefilled = ref(false);

async function fetchPackages() {
  if (!apiBase.value) return [];

  const url = joinUrl(apiBase.value, '/api/packages');
  const cookieHeaders = import.meta.server
    ? useRequestHeaders(['cookie'])
    : undefined;

  const ts = Date.now(); // cache-bust (CDN/edge)

  const res = await $fetch.raw(url, {
    method: 'GET',
    cache: 'no-store',
    credentials: 'include',
    headers: {
      ...(cookieHeaders || {}),
      Accept: 'application/json',
      'Accept-Language': currentLocale.value
    },
    query: { lang: currentLocale.value /*lng: currentLocale.value*/, ts } // ACHTUNG, HIER EIGENTLICH FALSCH TODO FIXME
  });

  return normalizePackages(res._data, currentLocale.value);
}

const {
  data: packages,
  pending: packagesPending,
  refresh: refreshPackages
} = await useAsyncData(
  () => packagesKey.value,
  async () => {
    try {
      const result = await fetchPackages();
      if (import.meta.server && (!result || result.length === 0))
        ssrResultWasEmpty.value = true;
      return result;
    } catch {
      if (import.meta.server) ssrResultWasEmpty.value = true;
      return [];
    }
  },
  { server: true, lazy: false, default: () => [], watch: [locale] }
);

async function clientRetryRefetchIfEmpty() {
  if (import.meta.server) return;
  if ((packages.value?.length ?? 0) > 0) return;

  const delays = [120, 260, 520];
  for (const d of delays) {
    try {
      await new Promise((r) => setTimeout(r, d));
      await refreshPackages();
      if ((packages.value?.length ?? 0) > 0) {
        clientRefilled.value = true;
        return;
      }
    } catch {
      // ignore
    }
  }
}

/* ---------- Build programs ---------- */
const programsAll = computed<ProgramLink[]>(() => {
  const list = packages.value || [];
  if (!list.length) return [];

  const bySlug = new Map<string, any>();
  for (const p of list) bySlug.set(p.slug, p);
  const uniq = Array.from(bySlug.values());
  uniq.sort(
    (a, b) => a.type.localeCompare(b.type) || a.title.localeCompare(b.title)
  );

  return uniq.map((p) => ({
    id: stableId(['program', currentLocale.value, p.type, p.slug]),
    label: p.title,
    link: `/program/${p.slug}`,
    type: p.type,
    groupLabel: t('packageType.' + p.type) || p.type
  }));
});

const programsGroups = computed(() => {
  const groups = new Map<
    string,
    { id: string; type: string; label: string; count: number }
  >();
  for (const p of programsAll.value) {
    if (!groups.has(p.type)) {
      groups.set(p.type, {
        id: stableId(['group', currentLocale.value, p.type]),
        type: p.type,
        label: t('packageType.' + p.type) || p.type,
        count: 0
      });
    }
    groups.get(p.type)!.count++;
  }
  return Array.from(groups.values()).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
});

const filteredPrograms = computed(() => {
  const q = programsQuery.value.trim().toLowerCase();
  const group = activeProgramGroup.value;

  return programsAll.value.filter((p) => {
    const okGroup = group === 'ALL' ? true : p.type === group;
    const okQuery = !q
      ? true
      : p.label.toLowerCase().includes(q) ||
        p.groupLabel.toLowerCase().includes(q);
    return okGroup && okQuery;
  });
});

const packagesCount = computed(() => programsAll.value.length);
const packagesSsrEmptyButClientFilled = computed(
  () => ssrResultWasEmpty.value && clientRefilled.value
);

/* Active / UX */
function normalizePath(p: string) {
  if (p !== '/' && p.endsWith('/')) return p.slice(0, -1);
  return p;
}
const currentPath = computed(() => normalizePath(route.path));
function isActivePath(path: string) {
  const cur = currentPath.value;
  if (path === '/') return cur === '/' || cur === `/${currentLocale.value}`;
  return cur.endsWith(normalizePath(path));
}
const getRouteBaseName = useRouteBaseName();
// Pagination positions and category slugs are not translation identities.
const isBlogListing = computed(() => getRouteBaseName(route) === 'blog');
const isStoriesActive = computed(() => getRouteBaseName(route) === 'stories');
const isProgramsActive = computed(() =>
  currentPath.value.includes('/program/')
);
const isToolsActive = computed(() => {
  const cur = currentPath.value;
  return (
    cur.includes('/product-suggestion') ||
    cur.includes('/daily-calorie-requirements')
  );
});

const localeDisplayOrder = [
  'tr',
  'en',
  'de',
  'fr',
  'es',
  'pt',
  'it',
  'ar',
  'hi',
  'ur',
  'ru',
  'ja',
  'zh'
];

const availableLocales = computed<string[]>(() => {
  const codes = (nuxtApp.$i18n.locales?.value || [])
    .map((l: any) => normalizeLocaleCode(l.code))
    // SwitchLocalePathLink resolves late SSR params and marks unsupported
    // translations disabled. Do not pre-filter with early layout-level params.
    .filter((code: string) => code !== currentLocale.value);

  return [...new Set(codes)].sort((a, b) => {
    const ai = localeDisplayOrder.indexOf(a);
    const bi = localeDisplayOrder.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });
});

// Keep native link navigation (including open-in-new-tab) intact. The official
// component owns URL resolution; the existing locale watcher owns data refresh.
function rememberLanguage(event: MouseEvent, code: string) {
  const link = event.currentTarget as HTMLAnchorElement;
  if (
    link?.hasAttribute('data-i18n-disabled') ||
    link?.getAttribute('href') === '#'
  ) {
    event.preventDefault();
    return;
  }
  if (event.defaultPrevented) return;
  // Preference cookies are shared by tabs. Remember an explicit flag choice
  // even when opening a new tab/context menu, or an English root link could
  // otherwise be redirected back to the previously selected language.
  nuxtApp.$i18n.setLocaleCookie(code);
  if (
    event.type !== 'click' ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  )
    return;
  hideAll();
}

function logoutAndClose() {
  authLogout();
  nuxtApp.$notifier?.showMessage?.({
    content: t('signIn.logoutSuccessful'),
    color: 'success'
  });
  hideAll();
}

function hideDropdowns() {
  userMenuDropdown.value = false;
  languageDropdown.value = false;
}
function hideAll() {
  hideDropdowns();
  showMobileMenu.value = false;
  resetExpanded();
}
function toggleMobileMenu() {
  showMobileMenu.value = !showMobileMenu.value;
  if (showMobileMenu.value) {
    hideDropdowns();
    // default open programs for useful search
    setExpanded('m-programs', true);
  }
}

/* ---- Type tones ---- */
const typeTone = computed(() => ({
  full: {
    chip: 'bg-orange-500/15 text-orange-200 ring-orange-500/25',
    glow: 'bg-orange-500/30',
    catActive: 'bg-orange-500/15 text-orange-100 ring-orange-500/25',
    catIdle: 'text-white/80 hover:bg-white/5 hover:text-white',
    chipIdle:
      'bg-orange-500/10 text-orange-200/90 ring-orange-500/20 hover:bg-orange-500/15'
  },
  nutrition: {
    chip: 'bg-emerald-500/15 text-emerald-200 ring-emerald-500/25',
    glow: 'bg-emerald-500/30',
    catActive: 'bg-emerald-500/15 text-emerald-100 ring-emerald-500/25',
    catIdle: 'text-white/80 hover:bg-white/5 hover:text-white',
    chipIdle:
      'bg-emerald-500/10 text-emerald-200/90 ring-emerald-500/20 hover:bg-emerald-500/15'
  },
  training: {
    chip: 'bg-sky-500/15 text-sky-200 ring-sky-500/25',
    glow: 'bg-sky-500/30',
    catActive: 'bg-sky-500/15 text-sky-100 ring-sky-500/25',
    catIdle: 'text-white/80 hover:bg-white/5 hover:text-white',
    chipIdle:
      'bg-sky-500/10 text-sky-200/90 ring-sky-500/20 hover:bg-sky-500/15'
  }
}));

function toneFor(type: string) {
  return (
    (typeTone.value as any)[type] || {
      chip: 'bg-white/10 text-white/80 ring-white/10',
      glow: 'bg-white/15',
      catActive: 'bg-white/10 text-white ring-white/10',
      catIdle: 'text-white/80 hover:bg-white/5 hover:text-white',
      chipIdle: 'bg-white/5 text-white/70 ring-white/10 hover:bg-white/10'
    }
  );
}

function programBadgeClass(type: string) {
  return toneFor(type).chip;
}
function programGlowClass(type: string) {
  return toneFor(type).glow;
}
function categoryButtonClass(type: string) {
  const isActive = activeProgramGroup.value === type;
  const base = 'hover:scale-[1.01] active:scale-[0.99]';
  const tone = toneFor(type);
  return isActive
    ? `${tone.catActive} ring-1 ring-inset`
    : `${tone.catIdle} ${base}`;
}
function categoryChipClass(type: string) {
  const isActive = activeProgramGroup.value === type;
  const tone = toneFor(type);
  return isActive ? `${tone.catActive} ring-white/0` : `${tone.chipIdle}`;
}

/* Closing when clicking outside header */
function onDocumentPointerDown(e: PointerEvent) {
  const target = e.target as HTMLElement | null;
  if (!target) return;
  const headerEl = document.querySelector('header');
  if (headerEl && headerEl.contains(target)) return;
  hideAll();
}
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    const restoreLanguageFocus = languageDropdown.value;
    hideAll();
    if (restoreLanguageFocus) document.getElementById('language-menu')?.focus();
  }
}

watch(
  () => route.fullPath,
  () => {
    showMobileMenu.value = false;
    resetExpanded();
  }
);

watch(
  () => currentLocale.value,
  async () => {
    resetExpanded();
    hideDropdowns();
    await refreshPackages();
    await clientRetryRefetchIfEmpty();
  }
);

onMounted(async () => {
  document.addEventListener('pointerdown', onDocumentPointerDown, {
    passive: true
  });
  window.addEventListener('keydown', onKeydown);

  // Rescue direct load when SSR was empty
  await clientRetryRefetchIfEmpty();
});

onBeforeUnmount(() => {
  stopLanguageLinksRefresh();
  document.removeEventListener('pointerdown', onDocumentPointerDown);
  window.removeEventListener('keydown', onKeydown);
});
</script>

<style scoped>
.language-options {
  max-height: calc(100vh - 10rem);
  max-height: calc(100dvh - 10rem - env(safe-area-inset-bottom, 0px));
}
.language-options :deep([data-i18n-disabled]) {
  display: none;
}

.fade-scale-enter-active,
.fade-scale-leave-active {
  transition:
    opacity 140ms ease,
    transform 140ms ease;
}
.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.985);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 140ms ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition:
    max-height 240ms ease,
    opacity 180ms ease,
    transform 180ms ease;
}
.slide-down-enter-from,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-6px);
}
.slide-down-enter-to,
.slide-down-leave-from {
  max-height: 900px;
  opacity: 1;
  transform: translateY(0);
}
</style>
